import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Divider,
  alpha,
  Slide,
  useScrollTrigger,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LanguageIcon from '@mui/icons-material/Language';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';
import { useColorMode } from '../contexts/ColorModeContext';
import { styled } from '@mui/material/styles';

interface HideOnScrollProps {
  children: React.ReactElement;
}

function HideOnScroll(props: HideOnScrollProps) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' 
    ? alpha(theme.palette.background.paper, 0.8)
    : alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(8px)',
  color: theme.palette.mode === 'light' ? theme.palette.text.primary : theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.mode === 'light' 
    ? alpha(theme.palette.divider, 0.1)
    : alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2, 0),
  transition: 'all 0.3s ease-in-out',
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease-in-out',
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Playfair Display", serif',
  fontSize: '2.5rem',
  fontWeight: 700,
  letterSpacing: '0.1em',
  marginBottom: theme.spacing(1),
  background: theme.palette.mode === 'light'
    ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
    : `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Playfair Display", serif',
  fontStyle: 'italic',
  fontSize: '1.2rem',
  color: theme.palette.mode === 'light' 
    ? alpha(theme.palette.text.secondary, 0.8)
    : alpha(theme.palette.text.secondary, 0.8),
  transition: 'all 0.3s ease-in-out',
}));

const NavContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
}));

interface NavButtonProps {
  component?: React.ElementType;
  to?: string;
  className?: string;
  children: React.ReactNode;
}

const NavButton = styled(Button)<NavButtonProps>(({ theme }) => ({
  position: 'relative',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 500,
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease-in-out',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    width: 0,
    height: 2,
    backgroundColor: theme.palette.primary.main,
    transition: 'all 0.3s ease-in-out',
    transform: 'translateX(-50%)',
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    '&::after': {
      width: '80%',
    },
  },
  '&.active': {
    color: theme.palette.primary.main,
    '&::after': {
      width: '80%',
    },
  },
}));

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'tr', name: 'Türkçe' },
];

const Navigation: React.FC = () => {
  const { t } = useTranslation();
  const { toggleColorMode, mode } = useColorMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    handleLanguageMenuClose();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        {t('header.title')}
      </Typography>
      <Divider />
      <List>
        <ListItem button component={RouterLink} to="/" selected={isActive('/')}>
          <ListItemText primary={t('navigation.home')} />
        </ListItem>
        <ListItem button component={RouterLink} to="/teams" selected={isActive('/teams')}>
          <ListItemText primary={t('navigation.teams')} />
        </ListItem>
        <ListItem button component={RouterLink} to="/news" selected={isActive('/news')}>
          <ListItemText primary={t('navigation.allNews')} />
        </ListItem>
      </List>
      <Divider />
      <List>
        {languages.map((lang) => (
          <ListItem 
            button 
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={i18n.language === lang.code}
          >
            <ListItemText primary={lang.name} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <HideOnScroll>
      <Box>
        <StyledAppBar position="fixed" elevation={scrolled ? 4 : 0}>
          <StyledToolbar sx={{ 
            flexDirection: 'column', 
            alignItems: 'center', 
            p: 0,
            py: scrolled ? 1 : 2,
          }}>
            <TitleContainer sx={{ mb: scrolled ? 1 : 2 }}>
              <MainTitle variant="h1" sx={{ fontSize: scrolled ? '2rem' : '2.5rem' }}>
                {t('header.title')}
              </MainTitle>
              <Subtitle variant="subtitle1" sx={{ 
                fontSize: scrolled ? '1rem' : '1.2rem',
                opacity: scrolled ? 0.8 : 1,
              }}>
                {t('header.subtitle')}
              </Subtitle>
            </TitleContainer>

            <Box sx={{ 
              width: '100%', 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mt: scrolled ? 0 : 1,
              px: 2,
            }}>
              {!isMobile && (
                <NavContainer>
                  <NavButton
                    component={RouterLink}
                    to="/"
                    className={isActive('/') ? 'active' : ''}
                  >
                    {t('navigation.home')}
                  </NavButton>
                  <NavButton
                    component={RouterLink}
                    to="/teams"
                    className={isActive('/teams') ? 'active' : ''}
                  >
                    {t('navigation.teams')}
                  </NavButton>
                  <NavButton
                    component={RouterLink}
                    to="/news"
                    className={isActive('/news') ? 'active' : ''}
                  >
                    {t('navigation.allNews')}
                  </NavButton>
                </NavContainer>
              )}

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                '& .MuiIconButton-root': {
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                },
              }}>
                <IconButton
                  onClick={handleLanguageMenuOpen}
                  color="inherit"
                  aria-label="select language"
                  aria-controls="language-menu"
                  aria-haspopup="true"
                >
                  <LanguageIcon />
                </IconButton>
                <Menu
                  id="language-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleLanguageMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      mt: 1,
                      borderRadius: 2,
                      '& .MuiMenuItem-root': {
                        px: 2,
                        py: 1,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      },
                    },
                  }}
                >
                  {languages.map((lang) => (
                    <MenuItem
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      selected={i18n.language === lang.code}
                    >
                      {lang.name}
                    </MenuItem>
                  ))}
                </Menu>
                <IconButton 
                  sx={{ ml: 1 }} 
                  onClick={toggleColorMode} 
                  color="inherit"
                >
                  {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
                {isMobile && (
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ ml: 1 }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          </StyledToolbar>
        </StyledAppBar>
        {isMobile && (
          <Drawer
            variant="temporary"
            anchor="right"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: 240,
                backgroundColor: theme.palette.background.paper,
                borderLeft: `1px solid ${theme.palette.divider}`,
              },
            }}
          >
            {drawer}
          </Drawer>
        )}
      </Box>
    </HideOnScroll>
  );
};

export default Navigation; 